(function () {
	'use strict';

	var config = window.fcaiChat || {};

	// However fast the reply actually arrives, keep the typing indicator up
	// for at least this long — an instant swap to text reads as unnatural.
	var MIN_LOADER_MS = 600;

	function h(tag, className, children) {
		var el = document.createElement(tag);
		if (className) el.className = className;
		if (children) {
			(Array.isArray(children) ? children : [children]).forEach(function (child) {
				if (typeof child === 'string') {
					el.appendChild(document.createTextNode(child));
				} else if (child) {
					el.appendChild(child);
				}
			});
		}
		return el;
	}

	function escapeHtml(str) {
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	// Trailing punctuation belongs to the sentence, not the address:
	// "escríbenos a hola@x.com." must not link the final period.
	function splitTrailingPunctuation(value) {
		var match = /[.,;:!?)\]]+$/.exec(value);
		if (!match) return [value, ''];
		return [value.slice(0, match.index), value.slice(match.index)];
	}

	// Full URLs read badly inside a chat bubble, so the visible label drops
	// the scheme and any trailing slash — the href keeps the real address.
	function linkLabel(url) {
		return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
	}

	// Only http(s) opens in a new tab: a mailto: handed to target="_blank"
	// leaves an empty tab behind in several browsers once the mail client
	// takes over.
	function anchor(href, label) {
		var newTab = /^https?:\/\//i.test(href) ? ' target="_blank" rel="noopener noreferrer"' : '';

		return '<a class="fcai-widget__message-link" href="' + href + '"' + newTab + '>' + label + '</a>';
	}

	function escapeRegExp(value) {
		return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}

	// Matches only the exact phone strings the admin configured (see
	// Widget::get_contact_phone_numbers() for why this is a list and not a
	// pattern). Longest first, so a number that contains another one as a
	// prefix still wins.
	function buildPhonePattern(phones) {
		if (!phones || !phones.length) return null;

		var escaped = phones
			.slice()
			.sort(function (a, b) {
				return b.length - a.length;
			})
			.map(function (phone) {
				return escapeRegExp(escapeHtml(phone));
			});

		// The leading group keeps whatever character preceded the number, so a
		// longer digit string never matches on one of its fragments. No
		// lookbehind — Safari only gained it recently and this ships to
		// visitors, not developers.
		return new RegExp('(^|[^\\d])(' + escaped.join('|') + ')(?!\\d)', 'g');
	}

	var phonePattern = buildPhonePattern(config.contactPhones);

	// Turns bare addresses the assistant wrote as plain text into real links.
	// Needed because the model writes contact details however it likes — in
	// practice usually as plain "https://..." or "name@example.com" rather
	// than Markdown — which left visitors having to select and copy the text
	// by hand, worst of all on a phone.
	//
	// Runs on already-escaped HTML that may already contain our own anchors
	// (from the Markdown link syntax above), so it splits those out first and
	// only ever rewrites the text between them: a URL that is already linked
	// must never be linked a second time.
	function linkifyBareAddresses(html) {
		return html
			.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/g)
			.map(function (part, index) {
				// Odd indexes are the captured anchors themselves — leave alone.
				if (index % 2 === 1) return part;

				part = part
					.replace(/https?:\/\/[^\s<>"']+/g, function (match) {
						var pieces = splitTrailingPunctuation(match);
						if (!pieces[0]) return match;
						return anchor(pieces[0], linkLabel(pieces[0])) + pieces[1];
					})
					.replace(/[^\s<>"'@]+@[^\s<>"'@]+\.[^\s<>"'@]+/g, function (match) {
						var pieces = splitTrailingPunctuation(match);
						if (!pieces[0]) return match;
						return anchor('mailto:' + pieces[0], pieces[0]) + pieces[1];
					});

				if (phonePattern) {
					part = part.replace(phonePattern, function (_match, before, phone) {
						// tel: wants the dialable digits only; the label keeps
						// the spacing the admin typed, which is what makes a
						// number readable.
						var dialable = phone.replace(/[^\d+]/g, '');
						return before + anchor('tel:' + dialable, phone);
					});
				}

				return part;
			})
			.join('');
	}

	// Applies inline formatting to an already-HTML-escaped line. Only ever
	// introduces our own fixed set of tags around escaped text, so there is
	// no way for AI-generated content to inject arbitrary markup — this must
	// run on already-escaped input, never on raw text.
	function inlineMarkdown(escapedLine) {
		escapedLine = escapedLine.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
		escapedLine = escapedLine.replace(/__([^_]+)__/g, '<strong>$1</strong>');
		escapedLine = escapedLine.replace(/\*([^*]+)\*/g, '<em>$1</em>');
		escapedLine = escapedLine.replace(/_([^_]+)_/g, '<em>$1</em>');
		escapedLine = escapedLine.replace(/`([^`]+)`/g, '<code>$1</code>');
		// Only http(s) links — rejects javascript:/data: etc. in the href.
		escapedLine = escapedLine.replace(
			/\[([^\[\]]+)\]\((https?:\/\/[^\s()]+)\)/g,
			function (_match, label, url) {
				return anchor(url, label);
			}
		);
		return linkifyBareAddresses(escapedLine);
	}

	// Minimal, safe Markdown-to-HTML: escapes the raw text first, then only
	// ever wraps it in our own fixed tags (strong/em/code/a/ul/li/p). Never
	// pass unescaped text into inlineMarkdown, and never use a real HTML
	// parser/eval on the AI's output.
	function markdownToHtml(rawText) {
		var lines = escapeHtml(rawText).split('\n');
		var htmlParts = [];
		var inList = false;

		function closeListIfOpen() {
			if (inList) {
				htmlParts.push('</ul>');
				inList = false;
			}
		}

		lines.forEach(function (line) {
			var trimmed = line.trim();
			var isListItem = /^[-*]\s+/.test(trimmed);

			if (isListItem) {
				if (!inList) {
					htmlParts.push('<ul>');
					inList = true;
				}
				htmlParts.push('<li>' + inlineMarkdown(trimmed.replace(/^[-*]\s+/, '')) + '</li>');
			} else if ('' === trimmed) {
				closeListIfOpen();
			} else {
				closeListIfOpen();
				htmlParts.push('<p>' + inlineMarkdown(trimmed) + '</p>');
			}
		});

		closeListIfOpen();

		return htmlParts.join('');
	}

	// Batches DOM/live-region updates instead of re-rendering on every SSE
	// chunk: streaming still looks near-real-time, but a screen reader gets
	// a handful of readable announcements instead of one per token, and an
	// in-progress markdown span (e.g. an unclosed "**") only flashes as
	// literal characters briefly instead of on every single token.
	function createBufferedRenderer(renderFn, intervalMs) {
		var raw = '';
		var pending = '';
		var timer = null;

		function flush() {
			if (pending) {
				raw += pending;
				pending = '';
				renderFn(raw);
			}
			timer = null;
		}

		return {
			push: function (chunk) {
				pending += chunk;
				if (!timer) {
					timer = setTimeout(flush, intervalMs);
				}
			},
			flush: flush,
			replace: function (text) {
				raw = text;
				renderFn(raw);
			}
		};
	}

	// The embedded config.nonce is only as fresh as the page it's baked
	// into — a full-page cache (LiteSpeed, WP Rocket, a CDN) can keep
	// serving that same HTML, dead nonce included, long after WordPress
	// itself invalidates it. When that happens, WordPress core rejects
	// every request with an error whose literal message is "Cookie check
	// failed" — indistinguishable, to a visitor, from the AI itself saying
	// that, since every one of this file's fetch() calls shows a JSON
	// error's `message` as if it were content. These two helpers replace
	// the stale nonce with a live one instead of trying to explain the
	// rejection: refreshNonce() re-mints it out of band via
	// config.nonceUrl (see ChatController::handle_nonce() for why that
	// route carries no nonce check of its own — asking it to prove a nonce
	// it exists to hand out would be circular), and fetchWithFreshNonce()
	// is a drop-in replacement for fetch() that does this once,
	// transparently, the moment a request is rejected specifically for
	// that reason — never for a real error, and never more than once, so a
	// genuinely broken connection still surfaces normally.
	var NONCE_ERROR_CODES = ['rest_cookie_invalid_nonce', 'fcai_invalid_nonce'];

	function refreshNonce() {
		if (!config.nonceUrl) return Promise.resolve(config.nonce);

		return fetch(config.nonceUrl)
			.then(function (response) {
				return response.ok ? response.json() : null;
			})
			.then(function (data) {
				if (data && data.nonce) config.nonce = data.nonce;
				return config.nonce;
			})
			.catch(function () {
				// Offline, or the endpoint itself is unreachable — keep
				// whatever nonce is already set rather than erroring here;
				// the caller's own request will surface the real problem.
				return config.nonce;
			});
	}

	function isStaleNonceResponse(response) {
		if (response.status !== 403) return Promise.resolve(false);

		// .clone() so a "no" leaves the original response body untouched
		// for the caller to read normally — Response bodies can only be
		// consumed once.
		return response
			.clone()
			.json()
			.then(function (data) {
				return !!(data && NONCE_ERROR_CODES.indexOf(data.code) !== -1);
			})
			.catch(function () {
				return false;
			});
	}

	function fetchWithFreshNonce(url, options) {
		options = options || {};

		function attempt() {
			// Any caller-supplied 'X-WP-Nonce' is copied in first and then
			// overwritten below — deliberately, so this always sends
			// whatever config.nonce holds *right now* (which refreshNonce()
			// may have just updated) rather than a value a caller captured
			// earlier into its own options object and could otherwise let
			// win the merge.
			var headers = {};
			for (var key in options.headers) {
				if (Object.prototype.hasOwnProperty.call(options.headers, key)) {
					headers[key] = options.headers[key];
				}
			}
			headers['X-WP-Nonce'] = config.nonce;

			var merged = {};
			for (var optKey in options) {
				if (Object.prototype.hasOwnProperty.call(options, optKey)) {
					merged[optKey] = options[optKey];
				}
			}
			merged.headers = headers;

			return fetch(url, merged);
		}

		return attempt().then(function (response) {
			return isStaleNonceResponse(response).then(function (isStale) {
				return isStale ? refreshNonce().then(attempt) : response;
			});
		});
	}

	function buildWidget() {
		var root = document.getElementById('fcai-chat-widget-root');
		if (!root) return;

		var isOpen = false;
		var hasMessages = false;

		// Every fixed (non-admin-authored) piece of on-screen text lives in
		// this one object — see SettingsPage::default_widget_labels() for
		// the PHP-side source of truth and Widget::get_widget_labels() for
		// how it's localized here. The `|| 'fallback'` on every use below
		// only matters pre-save/pre-upgrade, when the option doesn't exist
		// yet; once saved, fcai_widget_labels always has every key filled.
		var labels = config.labels || {};

		var botName = config.botName || 'Fanci';

		var bubble = h('button', 'fcai-widget__bubble');
		bubble.type = 'button';
		bubble.setAttribute('aria-haspopup', 'dialog');
		bubble.setAttribute('aria-expanded', 'false');
		bubble.setAttribute('aria-controls', 'fcai-chat-window');
		bubble.setAttribute('aria-label', labels.openChatLabel || 'Open chat');

		if (config.botAvatarUrl) {
			var bubbleAvatar = h('img', 'fcai-widget__bubble-avatar');
			bubbleAvatar.src = config.botAvatarUrl;
			bubbleAvatar.alt = '';
			bubble.appendChild(bubbleAvatar);
		}

		// Proactive teaser bubble (optional, off by default) — a floating
		// invitation shown once near the launcher before the visitor opens
		// the chat, distinct from the greeting message (which only shows
		// *after* opening). Built upfront (even though it may never become
		// visible) so aria-live can announce it reliably once shown — some
		// screen readers don't reliably announce a live region that's
		// inserted into the DOM already populated.
		var STORAGE_KEY_TEASER_DISMISSED = 'fcai_teaser_dismissed';
		var teaserEl = null;
		var teaserMessageBtn = null;
		var teaserCloseBtn = null;
		var teaserAlreadyDismissed = false;

		try {
			teaserAlreadyDismissed = sessionStorage.getItem(STORAGE_KEY_TEASER_DISMISSED) === '1';
		} catch (e) {
			// Storage unavailable — treat as not dismissed, worst case the
			// teaser can show more than once across a visit.
		}

		if (config.teaserEnabled && config.teaserMessage && !teaserAlreadyDismissed) {
			// {bot_name} substitution matches greetingText below — same
			// literal token, same reasoning (always reflects the current bot
			// name with no extra round trip).
			var teaserText = config.teaserMessage.replace(/\{bot_name\}/g, botName);

			teaserEl = h('div', 'fcai-widget__teaser');
			teaserEl.setAttribute('aria-live', 'polite');

			teaserMessageBtn = h('button', 'fcai-widget__teaser-message', teaserText);
			teaserMessageBtn.type = 'button';

			teaserCloseBtn = h('button', 'fcai-widget__teaser-close');
			teaserCloseBtn.type = 'button';
			teaserCloseBtn.setAttribute('aria-label', labels.teaserDismissLabel || 'Dismiss');
			teaserCloseBtn.textContent = '×';

			teaserEl.appendChild(teaserMessageBtn);
			teaserEl.appendChild(teaserCloseBtn);
		}

		var windowEl = h('div', 'fcai-widget__window');
		windowEl.id = 'fcai-chat-window';
		windowEl.style.display = 'none';
		windowEl.setAttribute('role', 'dialog');
		windowEl.setAttribute('aria-labelledby', 'fcai-chat-title');

		var headerAvatar = null;
		if (config.botAvatarUrl) {
			headerAvatar = h('img', 'fcai-widget__header-avatar');
			headerAvatar.src = config.botAvatarUrl;
			headerAvatar.alt = '';
		}

		var title = h('h2', 'fcai-widget__title', botName);
		title.id = 'fcai-chat-title';

		var closeBtn = h('button', 'fcai-widget__close');
		closeBtn.type = 'button';
		closeBtn.setAttribute('aria-label', labels.closeChatLabel || 'Close chat');
		closeBtn.textContent = '×';

		var headerChildren = headerAvatar ? [headerAvatar, title] : [title];

		// Builds a fresh wa.me URL from scratch (never string-mutates the
		// pre-built config.whatsappUrl) so an AI-generated summary can be
		// appended to the base message template on demand.
		function buildWhatsAppUrl(extraText) {
			var text = config.whatsappMessage || '';
			if (extraText) {
				text = text ? text + '\n\n' + extraText : extraText;
			}
			return 'https://wa.me/' + encodeURIComponent(config.whatsappNumber || '') +
				(text ? '?text=' + encodeURIComponent(text) : '');
		}

		if (config.whatsappUrl) {
			var whatsappLink = h('a', 'fcai-widget__whatsapp', labels.whatsappButton || 'WhatsApp');
			whatsappLink.href = config.whatsappUrl;
			whatsappLink.target = '_blank';
			whatsappLink.rel = 'noopener noreferrer';
			whatsappLink.setAttribute(
				'aria-label',
				labels.whatsappAriaLabel || 'Contact us on WhatsApp (opens in a new tab)'
			);

			// Only intervene when there's an existing conversation to summarize;
			// otherwise let the plain href navigate as normal.
			whatsappLink.addEventListener('click', function (e) {
				if (!hasMessages || !config.whatsappSummaryUrl) return;

				e.preventDefault();

				// Must open the tab synchronously within the click handler — opening
				// it after the async summary fetch resolves loses the user-gesture
				// context and gets blocked by popup blockers. Can't pass 'noopener'
				// here: per spec, window.open() always returns null when 'noopener'
				// is set, which would make the reference below unusable. Sever the
				// opener link manually instead, which gives the same tabnabbing
				// protection while keeping a usable reference.
				var newTab = window.open('', '_blank');
				if (!newTab) {
					window.open(config.whatsappUrl, '_blank', 'noopener,noreferrer');
					return;
				}
				try {
					newTab.opener = null;
				} catch (err) {
					// Some browsers don't allow reassigning opener — harmless.
				}

				fetchWithFreshNonce(config.whatsappSummaryUrl, {
					method: 'POST'
				})
					.then(function (response) {
						return response.ok ? response.json() : null;
					})
					.then(function (data) {
						var summary = data && data.summary;
						var prefix = labels.chatSummaryPrefix || 'Chat summary:';
						newTab.location = buildWhatsAppUrl(summary ? prefix + '\n' + summary : '');
					})
					.catch(function () {
						newTab.location = config.whatsappUrl;
					});
			});

			headerChildren.push(whatsappLink);
		}

		headerChildren.push(closeBtn);

		var header = h('div', 'fcai-widget__header', headerChildren);

		var body = h('div', 'fcai-widget__body');
		body.setAttribute('role', 'log');
		body.setAttribute('aria-live', 'polite');
		body.setAttribute('aria-label', labels.chatMessagesLabel || 'Chat messages');
		// Makes the transcript reachable/scrollable (arrow keys, Page Up/Down)
		// for keyboard-only users — it sits in the tab order below, between
		// the close button and the input.
		body.tabIndex = 0;

		// Shown until the first message (sent or restored from history)
		// appears — see the top of appendMessage() below, which removes it
		// unconditionally on either path rather than trying to know in
		// advance whether history restoration will find anything.
		var greetingEl = h('div', 'fcai-widget__greeting');

		if (config.botAvatarUrl) {
			var greetingAvatar = h('img', 'fcai-widget__greeting-avatar');
			greetingAvatar.src = config.botAvatarUrl;
			greetingAvatar.alt = '';
			greetingEl.appendChild(greetingAvatar);
		}

		// {bot_name} is a literal token (see SettingsPage::default_greeting_message())
		// the admin can use in their own custom greeting text; substituted
		// here rather than server-side so it always reflects the bot name
		// exactly as configured, with no extra round trip.
		var greetingText = (config.greetingMessage || 'Hi, I’m {bot_name}. How can I help you?').replace(
			/\{bot_name\}/g,
			botName
		);
		greetingEl.appendChild(h('p', 'fcai-widget__greeting-text', greetingText));

		if (config.suggestedQuestions && config.suggestedQuestions.length) {
			var chipsEl = h('div', 'fcai-widget__chips');

			config.suggestedQuestions.forEach(function (question) {
				var chip = h('button', 'fcai-widget__chip', question);
				chip.type = 'button';
				chip.addEventListener('click', function () {
					input.value = question;
					sendMessage();
				});
				chipsEl.appendChild(chip);
			});

			greetingEl.appendChild(chipsEl);
		}

		body.appendChild(greetingEl);

		var footer = h('div', 'fcai-widget__footer');
		var disclaimerEl = null;

		// The pre-chat contact gate (name/email/phone) and the normal chat
		// controls are both built upfront and toggled via display, rather
		// than inserted/removed — simpler to reason about, and keeps the
		// Tab order stable while whichever one is visible changes.
		var gateActive = Boolean(config.precontactEnabled);
		var gateEl = null;
		var nameInput, emailInput, phoneInput, consentCheckbox, continueBtn, skipBtn, gateErrorEl;

		if (config.precontactEnabled) {
			gateEl = h('div', 'fcai-widget__gate');

			if (config.precontactMessage) {
				gateEl.appendChild(h('p', 'fcai-widget__gate-intro', config.precontactMessage));
			}

			function buildGateField(id, labelText, type, autocomplete) {
				var wrapper = h('div', 'fcai-field');
				var label = h('label', 'fcai-field__label', labelText);
				label.setAttribute('for', id);
				var fieldInput = h('input', 'fcai-field__input');
				fieldInput.type = type;
				fieldInput.id = id;
				fieldInput.autocomplete = autocomplete;
				wrapper.appendChild(label);
				wrapper.appendChild(fieldInput);
				gateEl.appendChild(wrapper);
				return fieldInput;
			}

			nameInput = buildGateField('fcai-precontact-name', labels.nameLabel || 'Name', 'text', 'name');
			emailInput = buildGateField('fcai-precontact-email', labels.emailLabel || 'Email', 'email', 'email');
			phoneInput = buildGateField('fcai-precontact-phone', labels.phoneLabel || 'Phone', 'tel', 'tel');

			if (config.consentEnabled) {
				var consentWrapper = h('label', 'fcai-widget__consent');
				consentCheckbox = h('input');
				consentCheckbox.type = 'checkbox';
				consentCheckbox.id = 'fcai-precontact-consent';

				var consentTextParts = [config.consentText || ''];

				if (config.privacyPolicyUrl) {
					consentTextParts.push(' ');
					var privacyLink = h('a', null, labels.privacyPolicyLinkText || 'Privacy Policy');
					privacyLink.href = config.privacyPolicyUrl;
					privacyLink.target = '_blank';
					privacyLink.rel = 'noopener';
					consentTextParts.push(privacyLink);
				}

				consentWrapper.appendChild(consentCheckbox);
				consentWrapper.appendChild(h('span', 'fcai-widget__consent-text', consentTextParts));
				gateEl.appendChild(consentWrapper);
			}

			gateErrorEl = h('p', 'fcai-widget__gate-error');
			gateErrorEl.setAttribute('role', 'alert');
			gateEl.appendChild(gateErrorEl);

			var gateActions = h('div', 'fcai-widget__gate-actions');

			continueBtn = h(
				'button',
				'fcai-button fcai-button--primary fcai-button--sm',
				labels.continueButton || 'Continue'
			);
			continueBtn.type = 'button';
			gateActions.appendChild(continueBtn);

			// Skip only ever appears when the admin explicitly allowed it —
			// and never while consent is required, since it would let a
			// visitor start chatting having neither filled in contact
			// details nor agreed to the consent notice.
			if (config.allowSkip && !config.consentEnabled) {
				skipBtn = h('button', 'fcai-button fcai-button--ghost fcai-button--sm', labels.skipButton || 'Skip');
				skipBtn.type = 'button';
				gateActions.appendChild(skipBtn);
			}

			gateEl.appendChild(gateActions);
			footer.appendChild(gateEl);
		}

		var chatControls = h('div', 'fcai-widget__controls');

		var inputLabel = h('label', 'fcai-sr-only', labels.messageInputLabel || 'Message');
		inputLabel.setAttribute('for', 'fcai-chat-input');

		var input = h('textarea', 'fcai-widget__input');
		input.id = 'fcai-chat-input';
		input.rows = 1;
		input.placeholder = labels.messagePlaceholder || 'Write a message...';
		// Some host themes (e.g. Bricks) wrap their own reset in a CSS
		// @layer with `textarea { line-height: inherit !important }`.
		// Layered !important beats an unlayered stylesheet !important
		// regardless of selector specificity, so chat-widget.css's own
		// !important loses on those themes. An inline style set via
		// setProperty(..., 'important') is the only thing that reliably
		// wins in both cases — a plain inline style (or the shorthand
		// input.style.lineHeight = '...') is not !important and still
		// loses to the theme's rule.
		input.style.setProperty('line-height', 'var(--fcai-line-height-base)', 'important');

		// Grows the textarea to fit its content, up to the max-height set in
		// CSS — resetting to 'auto' first is what makes scrollHeight shrink
		// back down when text is removed, not just grow.
		function resizeInput() {
			input.style.height = 'auto';
			input.style.height = input.scrollHeight + 'px';
		}

		input.addEventListener('input', resizeInput);

		var sendBtn = h('button', 'fcai-widget__send');
		sendBtn.type = 'button';
		sendBtn.setAttribute('aria-label', labels.sendButton || 'Send');
		// Fixed icon, not AI/user-generated content — safe as static innerHTML.
		sendBtn.innerHTML =
			'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
			'<path d="M4 12L20 4L14 20L11 13L4 12Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>' +
			'</svg>';

		chatControls.appendChild(inputLabel);
		chatControls.appendChild(input);
		chatControls.appendChild(sendBtn);
		footer.appendChild(chatControls);

		// Placed after the input (not before it) — a footnote below the
		// conversation's primary action, matching how ChatGPT/Claude-style
		// chat UIs place their own AI disclaimer.
		if (config.disclaimer) {
			disclaimerEl = h('p', 'fcai-widget__disclaimer', config.disclaimer);
			footer.appendChild(disclaimerEl);
		}

		function setGateVisible(visible) {
			gateActive = visible;
			if (gateEl) gateEl.style.display = visible ? 'block' : 'none';
			// 'flex', not 'block' — .fcai-widget__controls is a flex row (the
			// textarea next to the send button); overwriting that with an
			// inline 'block' would collapse them onto separate lines instead.
			chatControls.style.display = visible ? 'none' : 'flex';
			// Keeping both the disclaimer and the gate's own intro message on
			// screen at once is a lot of text before the visitor has even
			// started chatting — the disclaimer only makes sense once there's
			// an actual conversation to disclaim about.
			if (disclaimerEl) disclaimerEl.style.display = visible ? 'none' : 'block';
			// Same reasoning applies to the greeting/chips: the gate already
			// has its own welcome message, and the window is too short to
			// show both without an awkward scroll.
			if (greetingEl) greetingEl.style.display = visible ? 'none' : 'flex';
		}

		// "Skip" has nothing to save server-side, unlike submitting the gate
		// (which restoreConversation() below detects via hasContactInfo) — so
		// without this, navigating away or reloading would show the gate
		// again despite the visitor already having answered it once.
		var STORAGE_KEY_GATE_SKIPPED = 'fcai_gate_skipped';

		function wasGateSkipped() {
			try {
				return sessionStorage.getItem(STORAGE_KEY_GATE_SKIPPED) === '1';
			} catch (e) {
				return false;
			}
		}

		function persistGateSkipped() {
			try {
				sessionStorage.setItem(STORAGE_KEY_GATE_SKIPPED, '1');
			} catch (e) {
				// Storage unavailable (privacy mode, etc.) — not critical.
			}
		}

		if (gateEl) {
			setGateVisible(!wasGateSkipped());

			continueBtn.addEventListener('click', function () {
				var name = nameInput.value.trim();
				var email = emailInput.value.trim();
				var phone = phoneInput.value.trim();
				var consent = consentCheckbox ? consentCheckbox.checked : false;

				// Continue always demands a complete form — "required" only
				// controls whether Skip exists as an alternative (below);
				// it never lets Continue itself accept a partial submission.
				if (!name || !email || !phone) {
					gateErrorEl.textContent = labels.requiredFieldsError || 'Please fill in all fields to continue.';
					return;
				}

				if (config.consentEnabled && !consent) {
					gateErrorEl.textContent = labels.consentRequiredError || 'Please accept the consent notice to continue.';
					return;
				}

				continueBtn.disabled = true;
				gateErrorEl.textContent = '';

				fetchWithFreshNonce(config.contactUrl, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ name: name, email: email, phone: phone, consent: consent })
				})
					.then(function (response) {
						return response
							.json()
							.catch(function () {
								return null;
							})
							.then(function (data) {
								return { ok: response.ok, data: data };
							});
					})
					.then(function (result) {
						if (!result.ok) {
							gateErrorEl.textContent =
								(result.data && result.data.message) ||
								labels.genericError ||
								'Something went wrong. Please try again.';
							continueBtn.disabled = false;
							return;
						}

						setGateVisible(false);
						input.focus();
					})
					.catch(function () {
						gateErrorEl.textContent = labels.connectionError || 'Connection error. Please try again.';
						continueBtn.disabled = false;
					});
			});

			if (skipBtn) {
				skipBtn.addEventListener('click', function () {
					persistGateSkipped();
					setGateVisible(false);
					input.focus();
				});
			}
		}

		windowEl.appendChild(header);
		windowEl.appendChild(body);
		windowEl.appendChild(footer);

		root.appendChild(windowEl);
		if (teaserEl) {
			root.appendChild(teaserEl);
		}
		root.appendChild(bubble);

		var STORAGE_KEY_OPEN = 'fcai_widget_open';

		function persistOpenState(open) {
			try {
				sessionStorage.setItem(STORAGE_KEY_OPEN, open ? '1' : '0');
			} catch (e) {
				// Storage unavailable (privacy mode, etc.) — not critical.
			}
		}

		// Matches .fcai-widget__window's transform transition duration in
		// chat-widget.css — display can't itself be transitioned, so the
		// close path waits for the fade/scale-out to finish before hiding
		// the element, instead of cutting it off mid-animation.
		var CLOSE_ANIMATION_MS =
			window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
		var closeAnimationTimer = null;

		// Sets the visual/ARIA state without moving focus — used both by user
		// actions (which additionally move focus, see openChat/closeChat) and
		// by history restoration on page load, where stealing focus on an
		// action the visitor didn't just take would be unexpected.
		function setOpenVisualState(open) {
			isOpen = open;

			if (closeAnimationTimer) {
				clearTimeout(closeAnimationTimer);
				closeAnimationTimer = null;
			}

			if (open) {
				windowEl.style.display = 'flex';
				// Forces layout before adding the --open class, so the browser
				// registers the initial (closed) transform/opacity first and
				// actually animates the transition instead of jumping straight
				// to the open state.
				void windowEl.offsetHeight;
				windowEl.classList.add('fcai-widget__window--open');
			} else {
				windowEl.classList.remove('fcai-widget__window--open');
				closeAnimationTimer = setTimeout(function () {
					windowEl.style.display = 'none';
					closeAnimationTimer = null;
				}, CLOSE_ANIMATION_MS);
			}

			bubble.setAttribute('aria-expanded', open ? 'true' : 'false');
			bubble.setAttribute(
				'aria-label',
				open ? labels.closeChatLabel || 'Close chat' : labels.openChatLabel || 'Open chat'
			);
		}

		// scrollHeight reads as 0 while the window is display:none, so any
		// scrollTop set while closed (e.g. during history restoration below)
		// is unreliable. Always re-scroll right after the window becomes
		// visible, once layout is valid, rather than trusting earlier attempts.
		function scrollToBottom() {
			body.scrollTop = body.scrollHeight;
		}

		// Scrolling to the bottom on every update (the norm for an ordinary
		// short reply) stops working once a single reply grows taller than
		// the entire visible conversation area — no scroll position can show
		// all of it at once, and always tracking the bottom leaves the
		// reply's own opening lines scrolled out of view above, forcing the
		// visitor to scroll back up to read it from the start. Once a
		// message's row crosses that height, this anchors to the row's own
		// top edge instead, so a long reply reads from the beginning by
		// default; short replies are unaffected — their row never crosses
		// that height threshold, so scrolling always falls through to the
		// same scrollToBottom() as before.
		function scrollForMessage(contentEl) {
			var rowEl = contentEl.closest('.fcai-widget__message-row');

			if (rowEl && rowEl.scrollHeight > body.clientHeight) {
				// offsetTop is relative to the nearest *positioned* ancestor,
				// which for these rows is .fcai-widget (position:fixed) rather
				// than body itself (position:static) — using it directly would
				// over-scroll by the header's own height, still cutting off the
				// reply's first lines. getBoundingClientRect() is relative to the
				// viewport regardless of positioning context, so diffing the two
				// rects (plus the container's current scrollTop) gives the row's
				// true offset within body's own scrollable content.
				var rowTop = rowEl.getBoundingClientRect().top;
				var bodyTop = body.getBoundingClientRect().top;
				body.scrollTop += rowTop - bodyTop;
			} else {
				scrollToBottom();
			}
		}

		function openChat() {
			setOpenVisualState(true);
			scrollToBottom();
			(gateActive && nameInput ? nameInput : input).focus();
			persistOpenState(true);
		}

		function closeChat() {
			setOpenVisualState(false);
			bubble.focus();
			persistOpenState(false);
		}

		bubble.addEventListener('click', function () {
			if (isOpen) {
				closeChat();
			} else {
				openChat();
			}
		});

		closeBtn.addEventListener('click', closeChat);

		if (teaserEl) {
			// display:none (not just opacity/pointer-events) is the base CSS
			// state, so a never-shown or already-dismissed teaser doesn't sit
			// in .fcai-widget's flex flow contributing two empty gaps around
			// itself (see the CSS comment). Toggled here in lockstep with the
			// --visible class, same technique setOpenVisualState() uses for
			// the window: flip display to flex and force a reflow *before*
			// the class add (so the fade-in transition actually plays), and
			// delay display:none on the way out until the fade-out finishes.
			var dismissTeaser = function () {
				teaserEl.classList.remove('fcai-widget__teaser--visible');
				setTimeout(function () {
					teaserEl.style.display = 'none';
				}, CLOSE_ANIMATION_MS);
				try {
					sessionStorage.setItem(STORAGE_KEY_TEASER_DISMISSED, '1');
				} catch (e) {
					// Storage unavailable — the teaser may reappear on the next
					// page load, not critical.
				}
			};

			teaserMessageBtn.addEventListener('click', function () {
				dismissTeaser();
				openChat();
			});

			teaserCloseBtn.addEventListener('click', dismissTeaser);

			// Any direct interaction with the launcher bubble itself (open OR
			// close) retires the teaser too — no reason to keep inviting a
			// click once the visitor has already engaged with it directly.
			bubble.addEventListener('click', dismissTeaser);

			setTimeout(function () {
				if (!isOpen && !hasMessages) {
					teaserEl.style.display = 'flex';
					void teaserEl.offsetHeight;
					teaserEl.classList.add('fcai-widget__teaser--visible');
				}
			}, Math.max(0, parseInt(config.teaserDelaySeconds, 10) || 0) * 1000);
		}

		// Traps Tab/Shift+Tab within the dialog's own controls while open.
		// This only constrains keyboard users (mouse/touch can always reach
		// the rest of the page regardless), and avoids Tab silently carrying
		// focus out to unrelated page content while the panel is open.
		// Escape remains the deliberate way out. Order matches DOM order so
		// Tab/Shift+Tab both feel natural, including the optional WhatsApp
		// link. Computed fresh on every Tab press (rather than once) since
		// which controls are focusable changes when the contact gate is
		// swapped out for the normal chat controls.
		function getFocusables() {
			// Excludes the title and avatar — neither is interactive, so
			// leaving them in would break the first/last wrap-around logic
			// below (Tab/Shift+Tab checks identity against these boundaries).
			var base = headerChildren.filter(function (el) {
				return el !== title && el !== headerAvatar;
			});

			if (gateActive) {
				var gateFocusables = [nameInput, emailInput, phoneInput, continueBtn];
				if (skipBtn) gateFocusables.push(skipBtn);
				return base.concat([body]).concat(gateFocusables);
			}

			return base.concat([body, input, sendBtn]);
		}

		document.addEventListener('keydown', function (e) {
			if (!isOpen) return;

			if (e.key === 'Escape') {
				closeChat();
				return;
			}

			if (e.key === 'Tab') {
				var focusables = getFocusables();
				var first = focusables[0];
				var last = focusables[focusables.length - 1];

				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		});

		function appendMessage(role, text) {
			if (greetingEl.parentNode) greetingEl.parentNode.removeChild(greetingEl);

			var speakerLabel = role === 'user' ? 'You said: ' : 'Assistant said: ';
			var rowEl = h('div', 'fcai-widget__message-row fcai-widget__message-row--' + role);

			if (role === 'assistant' && config.botAvatarUrl) {
				var avatarEl = h('img', 'fcai-widget__message-avatar');
				avatarEl.src = config.botAvatarUrl;
				avatarEl.alt = '';
				rowEl.appendChild(avatarEl);
			}

			var bubbleEl = h('div', 'fcai-widget__message fcai-widget__message--' + role);
			var srLabel = h('span', 'fcai-sr-only', speakerLabel);
			var contentEl = h('div', 'fcai-widget__message-content');

			contentEl.innerHTML = markdownToHtml(text || '');

			bubbleEl.appendChild(srLabel);
			bubbleEl.appendChild(contentEl);
			rowEl.appendChild(bubbleEl);
			body.appendChild(rowEl);
			body.scrollTop = body.scrollHeight;

			return contentEl;
		}

		function sendMessage() {
			var message = input.value.trim();
			if (!message) return;

			hasMessages = true;
			input.value = '';
			resizeInput();
			sendBtn.disabled = true;
			appendMessage('user', message);

			var assistantEl = appendMessage('assistant', '');
			assistantEl.innerHTML =
				'<span class="fcai-widget__typing" aria-hidden="true"><span></span><span></span><span></span></span>';

			var requestStartedAt = Date.now();
			var revealed = false;
			var revealTimer = null;
			var latestText = '';

			function showText(fullText) {
				assistantEl.innerHTML = markdownToHtml(fullText);
				scrollForMessage(assistantEl);
			}

			// The first swap from the typing indicator to real text is held
			// back until MIN_LOADER_MS has passed since the request started,
			// however fast the reply actually was. Every render before that
			// just updates latestText so the eventual reveal shows the
			// freshest content; renders after the reveal happen immediately.
			var buffered = createBufferedRenderer(function (fullText) {
				if (revealed) {
					showText(fullText);
					return;
				}

				latestText = fullText;
				if (revealTimer) return;

				var wait = Math.max(0, MIN_LOADER_MS - (Date.now() - requestStartedAt));
				revealTimer = setTimeout(function () {
					revealed = true;
					// The row itself already played its own entrance animation
					// back when it was inserted holding the typing indicator —
					// this is the separate animation for the moment real text
					// actually replaces those dots.
					assistantEl.classList.add('fcai-widget__message-content--reveal');
					showText(latestText);
				}, wait);
			}, 200);

			fetchWithFreshNonce(config.restUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ message: message })
			})
				.then(function (response) {
					if (!response.ok || !response.body) {
						return response
							.json()
							.catch(function () {
								return null;
							})
							.then(function (data) {
								buffered.replace(
									(data && data.message) || labels.genericError || 'Something went wrong. Please try again.'
								);
							});
					}

					var reader = response.body.getReader();
					var decoder = new TextDecoder();
					var chunkBuffer = '';

					// Whether the server sent anything renderable at all — a delta
					// or an error. Deliberately NOT the same as `revealed`: the
					// first render is held back by MIN_LOADER_MS, so an error
					// arriving quickly is still un-revealed when the stream ends,
					// and keying the fallback below off `revealed` would overwrite
					// a real error message ("out of credits", "temporarily
					// unavailable") with a generic "couldn't answer".
					var receivedSomething = false;

					function read() {
						return reader.read().then(function (result) {
							if (result.done) {
								buffered.flush();

								// The server can legitimately end a stream having sent
								// zero visible content and no error (a reasoning model
								// that spent its whole token budget thinking, say).
								// buffered.flush() is a no-op when nothing was ever
								// pushed, so without this the typing indicator would
								// stay on screen forever with no way to tell it's done.
								if (!receivedSomething) {
									buffered.replace(
										labels.emptyReplyError ||
											"I couldn't come up with an answer to that — could you rephrase your question?"
									);
								}

								return;
							}

							chunkBuffer += decoder.decode(result.value, { stream: true });
							var events = chunkBuffer.split('\n\n');
							chunkBuffer = events.pop() || '';

							events.forEach(function (rawEvent) {
								var line = rawEvent.replace(/^data:\s*/, '').trim();
								if (!line || line === '[DONE]') return;

								try {
									var payload = JSON.parse(line);
									if (payload.delta) {
										receivedSomething = true;
										buffered.push(payload.delta);
									} else if (payload.error) {
										receivedSomething = true;
										buffered.replace(payload.error);
									}
								} catch (e) {
									// Ignore malformed chunks.
								}
							});

							return read();
						});
					}

					return read();
				})
				.catch(function () {
					buffered.replace(labels.connectionError || 'Connection error. Please try again.');
				})
				.finally(function () {
					sendBtn.disabled = false;
				});
		}

		sendBtn.addEventListener('click', sendMessage);
		input.addEventListener('keydown', function (e) {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				sendMessage();
			}
		});

		// Restores the conversation (and whether the window was left open)
		// when the visitor navigates to another page — the server already
		// keeps the conversation via the session cookie; this just repaints
		// it into the newly-built widget on this page.
		function restoreConversation() {
			if (!config.historyUrl) return;

			fetchWithFreshNonce(config.historyUrl)
				.then(function (response) {
					return response.ok ? response.json() : null;
				})
				.then(function (data) {
					if (!data) return;

					// A visitor may have already submitted the pre-chat gate
					// (this page load or an earlier one, same session) without
					// having sent an actual message yet — don't ask again.
					// hasConsent covers the case where every contact field was
					// left blank (allowed when they're optional) but the
					// required consent checkbox was already checked.
					if ((data.hasContactInfo || data.hasConsent) && gateEl) {
						setGateVisible(false);
					}

					if (!data.messages || !data.messages.length) return;

					hasMessages = true;
					data.messages.forEach(function (message) {
						appendMessage(message.role, message.content);
					});

					var wasOpen = false;
					try {
						wasOpen = sessionStorage.getItem(STORAGE_KEY_OPEN) === '1';
					} catch (e) {
						// Storage unavailable — leave the widget closed.
					}

					if (wasOpen) {
						setOpenVisualState(true);
						scrollToBottom();
					}
				})
				.catch(function () {
					// No history available — start with an empty conversation.
				});
		}

		restoreConversation();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', buildWidget);
	} else {
		buildWidget();
	}
})();
